"use client";
import { useMutation } from "@apollo/client/react";
import { addBreadcrumb, captureMessage, setTag } from "@sentry/nextjs";
import { logger } from "@portal/app/_common/lib/logger";
import {
  EVERFLOW_CLICK_OFFER_COOKIE_NAME,
  EVERFLOW_LOADED_EVENT_NAME,
  EVERFLOW_OFFER_ID,
} from "@portal/app/_common/types/everflow";
import {
  detectInAppBrowser,
  getUserAgent,
  parseUaVersions,
} from "@portal/app/_common/lib/browserDetection";
import { isKnownAnalyticsCrawlerUserAgent } from "@portal/app/_common/lib/crawlers";
import EverflowSDK from "@everflow/everflow-sdk";
import cookies from "js-cookie";
import { parseEverflowAffiliateParamsFromSearch } from "@portal/app/_common/lib/everflowAffiliateParams";
import { COOKIE_KEYS, deleteCookie, setCookie } from "@portal/app/_common/lib/headers";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { gql } from "graphql-tag";
import { getEnvironmentName, isProduction } from "@portal/app/_common/lib/edgeUtils";
import { PersistEverflowTransactionIdDocument } from "./generated";
import {
  captureEverflowLanding,
  EverflowLanding,
  isEverflowTransactionId,
} from "./everflowLanding";

void gql`
  mutation persistEverflowTransactionId {
    persistEverflowTransactionId {
      status
      transactionId
    }
  }
`;

const PERSISTED_TID_SESSION_STORAGE_KEY = "dreambound:everflow:persistedTid";

/**
 * Matches the Everflow SDK's own 30-day TID cookie window so the affiliate
 * params survive as long as the transaction ID they belong to.
 */
const AFFILIATE_PARAMS_COOKIE_EXPIRES_DAYS = 30;

/**
 * Captures Everflow affiliate params (oid, affid, sub1-8) from the landing
 * URL into a first-party cookie. The query string is gone after the first
 * navigation, but leads are created pages later (marketplace intake, program
 * flow, clickout), so lead-creation surfaces read this cookie to stamp
 * enrollmentItem.metadata.tracking. Capture immediately when no previous click
 * exists. Otherwise replace this companion capture only when the selected click
 * succeeds. Clear old params if a successful replacement has none, so downstream
 * leads cannot mix different clicks. Always use the frozen landing URL.
 * Without SDK tracking, capture URL params independently as before.
 */
function persistAffiliateParamsFromSearch(search: string, clearIfMissing = false): void {
  const params = parseEverflowAffiliateParamsFromSearch(search);
  if (!params) {
    if (clearIfMissing && cookies.get(COOKIE_KEYS.EVERFLOW_AFFILIATE_PARAMS) !== undefined) {
      deleteCookie(COOKIE_KEYS.EVERFLOW_AFFILIATE_PARAMS);
    }
    return;
  }
  setCookie(COOKIE_KEYS.EVERFLOW_AFFILIATE_PARAMS, JSON.stringify(params), {
    expires: AFFILIATE_PARAMS_COOKIE_EXPIRES_DAYS,
  });
}

function readPersistedTid(): string | null {
  try {
    return window.sessionStorage.getItem(PERSISTED_TID_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writePersistedTid(tid: string): void {
  try {
    window.sessionStorage.setItem(PERSISTED_TID_SESSION_STORAGE_KEY, tid);
  } catch {
    // sessionStorage can throw in Safari private mode / some in-app browsers.
    // The in-memory ref still prevents double-fires within this page load.
  }
}

if (!process.env.EVERFLOW_TRACKING_DOMAIN && isProduction()) {
  throw new Error("EVERFLOW_TRACKING_DOMAIN is not set");
}

const trackingDomain = process.env.EVERFLOW_TRACKING_DOMAIN;
const isEverflowDisabled = trackingDomain?.includes("intentionally-fail");

type FailedStep =
  | "configure"
  | "cookie_write"
  | "click_threw"
  | "click_timeout"
  | "transactionId_empty";

/** Fraction of never-fired init cases to log (keeps volume bounded on fast navigations). */
const NEVER_FIRED_SAMPLE_RATE = 0.05;

/**
 * How long we wait before emitting the "capture timeout" warning if the SDK
 * still hasn't resolved. This is the upper bound on our tolerance for a slow
 * SDK response — form funnels that rely on the `ef_tid_c_o_<offer>` cookie
 * being present at submit won't recover past this window.
 */
const CAPTURE_TIMEOUT_MS = 30_000;

/**
 * Tracks whether `run()` has actually been called at least once so we can
 * detect "scheduled but never fired" cases on cleanup — users who navigate
 * away before requestIdleCallback / setTimeout fires.
 */
let runStarted = false;

/**
 * Transaction ID the SDK resolved with on success. Read by the capture-timeout
 * warning below to distinguish "SDK still hasn't resolved" from "SDK resolved
 * fine" at the 30s mark.
 */
let resolvedTid: string | undefined;

/**
 * Bound the wait without assuming a timed-out request was never recorded.
 * The original promise can still resolve and publish attribution after this.
 */
const SDK_CLICK_TIMEOUT_MS = 8_000;

/**
 * Bound configuration retries in restricted browsers.
 * New-click creation is never retried after a request has been dispatched.
 */
const IN_APP_BROWSER_MAX_RETRIES = 3;

/** Wraps a promise with a timeout to prevent indefinite hanging. */
function withTimeout<T>(promise: Promise<T>, ms: number, label = "Operation"): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    promise.then(
      (val) => {
        clearTimeout(timer);
        resolve(val);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** Returns Network Information API data when available (experimental API, not in all browsers). */
function getNetworkInfo(): Record<string, unknown> | undefined {
  const conn = (
    navigator as unknown as {
      connection?: { effectiveType?: string; downlink?: number; rtt?: number };
    }
  ).connection;
  if (!conn) return undefined;
  return {
    effectiveType: conn.effectiveType,
    downlink: conn.downlink,
    rtt: conn.rtt,
  };
}

/**
 * Cloudflare worker proxy in front of the Everflow tracking domain. The
 * primary custom domain (`e.dreambound.com`, an Everflow-managed endpoint) is
 * unreachable at the network level for a meaningful share of mobile clients —
 * plain Mobile Safari / Chrome Mobile as well as Facebook and Instagram in-app
 * browsers — while this first-party proxy answers for the same devices. It is
 * used for click creation and conversions. Redirect attribution is stored
 * locally and never sends a click request to either endpoint.
 */
const proxyDomain = process.env.EVERFLOW_PROXY_DOMAIN;

/**
 * How long requestIdleCallback waits before forcing execution. Keeps init
 * from being deferred indefinitely on busy pages.
 */
const EVERFLOW_IDLE_TIMEOUT_MS = 4_000;

/**
 * Short delay after window.load before running init when requestIdleCallback
 * is unavailable. Gives the browser a moment to settle after load fires.
 */
const EVERFLOW_LOAD_FALLBACK_DELAY_MS = 1_500;

/** Module-level guard so only the first mount schedules init (Strict Mode fires useEffect twice). */
let initScheduled = false;

/**
 * Module-level guard for the 30s capture-timeout warning. Scheduled once per
 * page load alongside init; emits a Sentry warning if the SDK still hasn't
 * resolved (no `resolvedTid`) after CAPTURE_TIMEOUT_MS. This used to live in
 * FormFunnelTrackingProvider but was funnel-scoped and duplicated the
 * cookie-jar inspection we already do here. Module-level is fine because
 * EverflowScript mounts once in the root layout.
 */
let captureTimeoutScheduled = false;
let captureTimeoutHandle: ReturnType<typeof setTimeout> | undefined;

function scheduleCaptureTimeoutWarning() {
  if (captureTimeoutScheduled) return;
  captureTimeoutScheduled = true;
  captureTimeoutHandle = setTimeout(() => {
    if (resolvedTid) return;
    const inAppBrowser = detectInAppBrowser();
    const ua = getUserAgent();
    const versions = parseUaVersions(ua);
    captureMessage("Everflow transaction ID capture timeout", {
      level: "warning",
      tags: {
        feature: "everflow",
        "everflow.outcome": "capture_timeout",
        ...(inAppBrowser && { inAppBrowser }),
        ...(versions.fbAppVersion && { fb_app_version: versions.fbAppVersion }),
        ...(versions.webviewChromeVersion && {
          webview_chrome_version: versions.webviewChromeVersion,
        }),
        ...(versions.iosWebKitVersion && { ios_webkit_version: versions.iosWebKitVersion }),
      },
      extra: {
        timeoutMs: CAPTURE_TIMEOUT_MS,
        runStarted,
        retryCount,
        lastFailedStep,
        userAgent: ua,
        sdkHasTransactionId: !!EverflowSDK.getTransactionId(EVERFLOW_OFFER_ID),
        cookiesEnabled: typeof navigator !== "undefined" ? navigator.cookieEnabled : undefined,
        pageUrl: typeof window !== "undefined" ? window.location.href : undefined,
      },
    });
  }, CAPTURE_TIMEOUT_MS);
}

/**
 * Capture local attribution immediately so hard navigation cannot drop a
 * redirect ID. Defer new-click requests using requestIdleCallback where
 * available, otherwise wait for window.load + a short delay.
 *
 * Returns a cleanup function that cancels any pending scheduling.
 */
function scheduleEverflowInit(): () => void {
  const landing = getLanding();
  if (initScheduled) return () => {};
  initScheduled = true;

  if (landing.mode !== "create") {
    runStarted = true;
    void everflowInit();
    return () => {};
  }

  let cancelled = false;
  let idleHandle: number | undefined;
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

  const run = () => {
    if (cancelled) return;
    runStarted = true;
    addBreadcrumb({
      category: "everflow",
      message: "Init starting",
      level: "info",
    });
    void everflowInit();
  };

  if (typeof window.requestIdleCallback === "function") {
    addBreadcrumb({
      category: "everflow",
      message: "Init scheduled via requestIdleCallback",
      level: "info",
      data: { timeoutMs: EVERFLOW_IDLE_TIMEOUT_MS },
    });
    idleHandle = window.requestIdleCallback(run, { timeout: EVERFLOW_IDLE_TIMEOUT_MS });
  } else if (document.readyState === "complete") {
    addBreadcrumb({
      category: "everflow",
      message: "Init scheduled via setTimeout (page already loaded)",
      level: "info",
      data: { delayMs: EVERFLOW_LOAD_FALLBACK_DELAY_MS },
    });
    timeoutHandle = setTimeout(run, EVERFLOW_LOAD_FALLBACK_DELAY_MS);
  } else {
    const onLoad = () => {
      addBreadcrumb({
        category: "everflow",
        message: "Init scheduled via load event + setTimeout fallback",
        level: "info",
        data: { delayMs: EVERFLOW_LOAD_FALLBACK_DELAY_MS },
      });
      timeoutHandle = setTimeout(run, EVERFLOW_LOAD_FALLBACK_DELAY_MS);
    };
    window.addEventListener("load", onLoad, { once: true });

    return () => {
      cancelled = true;
      initScheduled = false;
      window.removeEventListener("load", onLoad);
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
      maybeCaptureNeverFired("load_event_pending");
    };
  }

  return () => {
    cancelled = true;
    initScheduled = false;
    if (idleHandle !== undefined) window.cancelIdleCallback(idleHandle);
    if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    maybeCaptureNeverFired(idleHandle !== undefined ? "idle_pending" : "timeout_pending");
  };
}

/**
 * Captures (at a low sample rate) the case where init was scheduled but
 * never actually ran because the component unmounted / page navigated first.
 * Complements the failure-event capture — those only fire once init has
 * actually attempted and exhausted retries. This covers the "SDK never got
 * a chance to try" population that currently produces no telemetry.
 */
function maybeCaptureNeverFired(
  pendingKind: "idle_pending" | "timeout_pending" | "load_event_pending"
) {
  if (runStarted) return;
  if (Math.random() >= NEVER_FIRED_SAMPLE_RATE) return;
  const inAppBrowser = detectInAppBrowser();
  const ua = getUserAgent();
  const versions = parseUaVersions(ua);
  captureMessage("Everflow init scheduled but never fired before unmount", {
    level: "warning",
    tags: {
      feature: "everflow",
      "everflow.outcome": "never_fired",
      "everflow.pendingKind": pendingKind,
      ...(inAppBrowser && { inAppBrowser }),
      ...(versions.fbAppVersion && { fb_app_version: versions.fbAppVersion }),
      ...(versions.webviewChromeVersion && {
        webview_chrome_version: versions.webviewChromeVersion,
      }),
      ...(versions.iosWebKitVersion && { ios_webkit_version: versions.iosWebKitVersion }),
    },
    extra: { userAgent: ua, pageUrl: window.location.href },
  });
}

export const EverflowScript = () => {
  // Once landing attribution resolves, immediately persist its ID onto the
  // session customer. Firing here (not at form-funnel submit) means we
  // capture the TID regardless of whether the user ever submits — including
  // sessions that bounce before the cookie would have mattered. Guards
  // against redundant fires via an in-memory ref + sessionStorage (across
  // SPA navigations) and a server-side ALREADY_PERSISTED no-op.
  // An old cookie must not be persisted while a fresh landing is resolving.
  // This store also publishes readiness when local reuse leaves the cookie unchanged.
  const everflowTransactionId = useSyncExternalStore(
    subscribeToCapture,
    getResolvedTid,
    getServerResolvedTid
  );
  const persistedForTidRef = useRef<string | null>(null);
  const [persistEverflowTransactionId] = useMutation(PersistEverflowTransactionIdDocument);

  useEffect(() => {
    // Check in the browser so cached HTML stays identical for crawlers and
    // visitors. Crawlers must not create clicks or capture-timeout warnings.
    if (isKnownAnalyticsCrawlerUserAgent(getUserAgent())) return;
    if (!trackingDomain || isEverflowDisabled) {
      persistAffiliateParamsFromSearch(window.location.search);
      return;
    }
    scheduleCaptureTimeoutWarning();
    return scheduleEverflowInit();
  }, []);

  useEffect(() => {
    if (
      !everflowTransactionId ||
      EverflowSDK.getTransactionId(EVERFLOW_OFFER_ID) !== everflowTransactionId
    )
      return;
    if (persistedForTidRef.current === everflowTransactionId) return;
    if (readPersistedTid() === everflowTransactionId) {
      persistedForTidRef.current = everflowTransactionId;
      return;
    }

    persistedForTidRef.current = everflowTransactionId;
    persistEverflowTransactionId()
      .then(({ data }) => {
        const status = data?.persistEverflowTransactionId.status;
        if (status && ["PERSISTED", "UPDATED", "ALREADY_PERSISTED"].includes(status)) {
          writePersistedTid(everflowTransactionId);
        } else {
          persistedForTidRef.current = null;
        }
      })
      .catch((error) => {
        persistedForTidRef.current = null;
        logger.error(error, "Failed to persist Everflow transaction ID");
      });
  }, [everflowTransactionId, persistEverflowTransactionId]);

  return null;
};

const initialWaitTimeout = 500;
const timeoutIncrement = 500;
const maxRetries = 20;
let retryCount = 0;
let lastError: unknown;
let lastFailedStep: FailedStep | undefined;
let capturedLanding: EverflowLanding | undefined;
let previousCanonicalTid: string | undefined;
let initialization: Promise<void> | undefined;

function getResolvedTid() {
  return resolvedTid;
}

function getServerResolvedTid() {
  return undefined;
}

function subscribeToCapture(onChange: () => void) {
  window.addEventListener(EVERFLOW_LOADED_EVENT_NAME, onChange);
  return () => window.removeEventListener(EVERFLOW_LOADED_EVENT_NAME, onChange);
}

/** Match the SDK's last successfully decoded cookie when paths/domains overlap. */
function readCanonicalCookieValue(): string | undefined {
  let value: string | undefined;
  for (const cookie of document.cookie.split(";")) {
    const [name, rawValue] = cookie.split("=");
    if (rawValue === undefined) continue;
    try {
      if (decodeURIComponent(name.trim()) === EVERFLOW_CLICK_OFFER_COOKIE_NAME) {
        value = decodeURIComponent(rawValue.trim()).trim();
      }
    } catch {
      // The SDK skips malformed entries without discarding an earlier match.
    }
  }
  return value;
}

function readCanonicalTransactionId(): string | undefined {
  return readCanonicalCookieValue()?.split("|").at(-1);
}

function getLanding(): EverflowLanding {
  if (!capturedLanding) {
    const canonicalTid = readCanonicalTransactionId();
    const sdkTid = EverflowSDK.getTransactionId(EVERFLOW_OFFER_ID);
    previousCanonicalTid = isEverflowTransactionId(canonicalTid) ? canonicalTid : undefined;
    capturedLanding = captureEverflowLanding(canonicalTid, sdkTid);
    if (!previousCanonicalTid && !isEverflowTransactionId(sdkTid)) {
      // With no prior click to protect, retain known campaign fields even if
      // click creation is blocked, fails, or never runs before navigation.
      persistAffiliateParamsFromSearch(new URL(capturedLanding.url).search);
    }
    // Keep the previous click until a replacement is captured locally or the SDK
    // returns a new one. Eager customer persistence waits for readiness.
  }
  return capturedLanding;
}

function publishTransactionId(tid: string, landing: EverflowLanding, via: string) {
  if (capturedLanding !== landing) return;
  if (!isEverflowTransactionId(tid) || EverflowSDK.getTransactionId(EVERFLOW_OFFER_ID) !== tid) {
    throw new Error("Everflow did not persist a valid transaction ID for the landing");
  }
  if (resolvedTid === tid) return;
  if ((landing.mode === "create" || landing.mode === "existing") && via !== "cookie_fallback") {
    persistAffiliateParamsFromSearch(new URL(landing.url).search, true);
  }
  resolvedTid = tid;
  setTag("everflow.resolvedVia", via);
  addBreadcrumb({
    category: "everflow",
    message: "Transaction ID captured",
    level: "info",
    data: { mode: landing.mode, via, retryCount },
  });
  window.dispatchEvent(new CustomEvent(EVERFLOW_LOADED_EVENT_NAME, { detail: tid }));
}

function publishPreviousTransactionId(landing: EverflowLanding) {
  if (
    previousCanonicalTid &&
    readCanonicalTransactionId() === previousCanonicalTid &&
    EverflowSDK.getTransactionId(EVERFLOW_OFFER_ID) === previousCanonicalTid
  ) {
    publishTransactionId(previousCanonicalTid, landing, "cookie_fallback");
  }
}

/** Accept an upstream click ID as attribution without firing another click. */
function persistLocalTransactionId(tid: string) {
  if (readCanonicalTransactionId()?.toLowerCase() === tid.toLowerCase()) return;
  const history = (readCanonicalCookieValue() || "").split("|").filter(isEverflowTransactionId);
  history.push(tid);
  // Match the SDK's cookie size bound, keeping the first and latest IDs.
  const boundedHistory = history.length > 50 ? [history[0], ...history.slice(-49)] : history;
  cookies.set(EVERFLOW_CLICK_OFFER_COOKIE_NAME, boundedHistory.join("|"), {
    expires: AFFILIATE_PARAMS_COOKIE_EXPIRES_DAYS,
    path: "/",
    sameSite: "lax",
    secure: window.location.protocol === "https:",
  });
  if (readCanonicalTransactionId()?.toLowerCase() !== tid.toLowerCase()) {
    throw new Error("Could not persist Everflow landing transaction ID");
  }
}

function reportCaptureFailure(landing: EverflowLanding, outcome: string, usePreviousClick = true) {
  captureMessage("Everflow transaction ID capture failed", {
    level: "warning",
    tags: {
      feature: "everflow",
      "everflow.mode": landing.mode,
      "everflow.outcome": outcome,
      "everflow.failedStep": lastFailedStep,
    },
    extra: {
      retryCount,
      lastError: errorMessage(lastError),
      landingUrl: landing.url,
      networkInfo: getNetworkInfo(),
    },
  });
  if (usePreviousClick) publishPreviousTransactionId(landing);
}

/** Single flight for this landing, including completed/failed attempts and remounts. */
export function everflowInit(): Promise<void> {
  if (isKnownAnalyticsCrawlerUserAgent(getUserAgent())) return Promise.resolve();
  const landing = getLanding();
  initialization ??= initializeLanding(landing);
  return initialization;
}

async function initializeLanding(landing: EverflowLanding): Promise<void> {
  if (landing.recoveredMalformedId) {
    captureMessage("Everflow malformed transaction ID recovery", {
      level: "warning",
      tags: {
        feature: "everflow",
        "everflow.outcome": "invalid_landing_transaction_id_recovery",
        "everflow.mode": landing.mode,
      },
    });
  }
  const inAppBrowser = detectInAppBrowser();
  const effectiveMaxRetries = inAppBrowser ? IN_APP_BROWSER_MAX_RETRIES : maxRetries;
  const usingProxy = !!proxyDomain;
  // Local attribution still needs SDK configuration for subsequent conversions.
  for (;;) {
    lastFailedStep = undefined;
    try {
      EverflowSDK.configure({ tracking_domain: proxyDomain || trackingDomain });
      break;
    } catch (error) {
      lastError = error;
      lastFailedStep = "configure";
      if (retryCount >= effectiveMaxRetries) {
        reportCaptureFailure(landing, "retries_exhausted");
        return;
      }
      await waitBeforeRetry();
    }
  }

  if (landing.mode === "invalid") {
    lastError = new Error("Invalid Everflow transaction ID on the landing URL");
    reportCaptureFailure(landing, "invalid_landing_transaction_id");
    return;
  }
  if (landing.mode === "returning") {
    try {
      publishTransactionId(landing.transactionId, landing, "cookie");
    } catch (error) {
      lastError = error;
      lastFailedStep = "cookie_write";
      reportCaptureFailure(landing, "returning_capture_failed");
    }
    return;
  }
  if (landing.mode === "existing" || landing.mode === "legacy") {
    try {
      // A syntactically valid redirect ID is accepted from upstream, not verified
      // by Everflow. SDK matching would still send another /sdk/click request.
      persistLocalTransactionId(landing.transactionId);
      publishTransactionId(landing.transactionId, landing, "local");
    } catch (error) {
      lastError = error;
      lastFailedStep = "cookie_write";
      reportCaptureFailure(landing, "local_capture_failed");
    }
    return;
  }

  let settled = false;
  let timedOutWhilePending = false;
  try {
    const click = EverflowSDK.click({
      ...landing.options,
      parameters: {
        ...landing.options.parameters,
        environment: getEnvironmentName(),
      },
    })
      .then((tid: string) => {
        settled = true;
        if (tid) publishTransactionId(tid, landing, usingProxy ? "proxy" : "primary");
        else if (timedOutWhilePending) publishPreviousTransactionId(landing);
        return tid;
      })
      .catch((error: unknown) => {
        settled = true;
        if (timedOutWhilePending) publishPreviousTransactionId(landing);
        throw error;
      });
    // Observe the raw promise so a late response still completes capture.
    // A timeout never cancels the request and is never permission to send another.
    const tid = await withTimeout(click, SDK_CLICK_TIMEOUT_MS, "EverflowSDK.click()");
    if (tid) return;
    lastFailedStep = "transactionId_empty";
    throw new Error("Everflow returned no transaction ID");
  } catch (error) {
    lastError = error;
    lastFailedStep ??= errorMessage(error).includes("timed out after")
      ? "click_timeout"
      : "click_threw";
    logger.error(error);
    // A pending request may still replace the cookie. Publish fallback only
    // after it settles, so persistence cannot race a late successful response.
    timedOutWhilePending = lastFailedStep === "click_timeout" && !settled;
    reportCaptureFailure(landing, "retry_suppressed", !timedOutWhilePending);
  }
}

async function waitBeforeRetry() {
  retryCount++;
  addBreadcrumb({
    category: "everflow",
    message: "Retrying SDK configuration",
    level: "info",
    data: { retryCount, failedStep: lastFailedStep },
  });
  await new Promise<void>((resolve) =>
    setTimeout(resolve, initialWaitTimeout + (retryCount - 1) * timeoutIncrement)
  );
}

/** Reset module state between isolated SDK tests; production captures one landing per page. */
export function resetRetryCount() {
  retryCount = 0;
  lastError = undefined;
  lastFailedStep = undefined;
  capturedLanding = undefined;
  previousCanonicalTid = undefined;
  initialization = undefined;
  resolvedTid = undefined;
  runStarted = false;
  initScheduled = false;
  captureTimeoutScheduled = false;
  if (captureTimeoutHandle) clearTimeout(captureTimeoutHandle);
  captureTimeoutHandle = undefined;
}
